/**
 * Código Morse internacional: tabela, timing PARIS-padrão e geração dos
 * passos de tom/silêncio que tocam o CW de verdade.
 *
 * Não é um sintetizador novo: `textToSteps` devolve a MESMA forma que
 * `RogerStep` já usa (`{freq_hz, ms}`), então quem manda toca com
 * `AudioEngine.playTone` — o método que já existe pros bipes de roger, que
 * já sabe ligar/desligar um oscilador nesses tempos e já sabe mandar o
 * resultado pra trilha de WebRTC quando `transmit=true`. Um passo com
 * `freq_hz: 0` já vira silêncio pela duração indicada, sem código novo.
 *
 * A mesma tabela também alimenta o quadro de referência visual no Dock —
 * é o que torna isto material de estudo, não só um efeito sonoro.
 */

export const MORSE_TABLE: Record<string, string> = {
  A: '.-',
  B: '-...',
  C: '-.-.',
  D: '-..',
  E: '.',
  F: '..-.',
  G: '--.',
  H: '....',
  I: '..',
  J: '.---',
  K: '-.-',
  L: '.-..',
  M: '--',
  N: '-.',
  O: '---',
  P: '.--.',
  Q: '--.-',
  R: '.-.',
  S: '...',
  T: '-',
  U: '..-',
  V: '...-',
  W: '.--',
  X: '-..-',
  Y: '-.--',
  Z: '--..',
  '0': '-----',
  '1': '.----',
  '2': '..---',
  '3': '...--',
  '4': '....-',
  '5': '.....',
  '6': '-....',
  '7': '--...',
  '8': '---..',
  '9': '----.',
  '.': '.-.-.-',
  ',': '--..--',
  '?': '..--..',
  '/': '-..-.',
  '=': '-...-',
}

/** Tom clássico de sidetone de CW. Fixo por enquanto — ajustar é refinamento
 * futuro, não essencial pra usar o modo. */
export const CW_TONE_HZ = 600

/** Passo de tom ou silêncio — mesma forma que RogerStep (web/src/lib/audio.ts). */
export interface MorseStep {
  freq_hz: number
  ms: number
}

/** Duração do ponto em ms, pela fórmula PARIS padrão (a palavra de calibração
 * que dá 1200/wpm ms por ponto). */
export function ditMs(wpm: number): number {
  return 1200 / Math.max(1, wpm)
}

/**
 * Gera os passos de tom/silêncio de um texto de Morse INCREMENTALMENTE, um
 * caractere de cada vez — é a mesma máquina de estado que `textToSteps` usa
 * (gap pendente antes do próximo caractere real: 0 no início, gapWord logo
 * depois de um espaço, sem acumular com espaços repetidos), só que exposta
 * caractere a caractere em vez de exigir o texto inteiro de uma vez.
 *
 * É o que o modo avançado de CW usa: cada tecla já gera os passos daquela
 * letra na hora, sem esperar a frase inteira pra tocar algo.
 */
export class MorseStreamer {
  private readonly dit: number
  private hasPlayed = false
  private pendingGap = 0

  constructor(wpm: number) {
    this.dit = ditMs(wpm)
  }

  /** Passos de UM caractere — vazio para espaço redundante ou símbolo sem
   * representação na tabela (mesma tolerância de textToSteps). */
  next(rawChar: string): MorseStep[] {
    const dit = this.dit
    const dah = dit * 3
    const gapSymbol = dit
    const gapChar = dit * 3
    const gapWord = dit * 7

    const c = rawChar.toUpperCase()
    if (c === ' ') {
      this.pendingGap = gapWord
      return []
    }
    const code = MORSE_TABLE[c]
    if (!code) return []

    const steps: MorseStep[] = []
    if (this.pendingGap > 0) {
      steps.push({ freq_hz: 0, ms: this.pendingGap })
    } else if (this.hasPlayed) {
      steps.push({ freq_hz: 0, ms: gapChar })
    }
    this.pendingGap = 0
    this.hasPlayed = true

    for (let j = 0; j < code.length; j++) {
      if (j > 0) steps.push({ freq_hz: 0, ms: gapSymbol })
      steps.push({ freq_hz: CW_TONE_HZ, ms: code[j] === '-' ? dah : dit })
    }
    return steps
  }
}

/**
 * Converte texto em passos de tom/silêncio numa velocidade (WPM).
 *
 * Caracteres sem representação na tabela são ignorados silenciosamente — o
 * texto já deveria ter passado por uma normalização antes (ver
 * internal/channel/hub.go: normalizeCWText), mas esta função não confia
 * nisso e não trava por causa de um caractere estranho.
 */
export function textToSteps(text: string, wpm: number): MorseStep[] {
  const streamer = new MorseStreamer(wpm)
  const steps: MorseStep[] = []
  for (const rawChar of text) steps.push(...streamer.next(rawChar))
  return steps
}

/** Duração total de tocar um texto numa velocidade — usado pra saber quando
 * a transmissão termina e soltar o PTT automaticamente. */
export function totalDurationMs(text: string, wpm: number): number {
  return textToSteps(text, wpm).reduce((sum, s) => sum + s.ms, 0)
}

/** Tabela invertida (".-" → "A") — construída uma vez, usada por ManualKeyer
 * pra decodificar o padrão de pontos/traços de volta em letra. */
const REVERSE_MORSE: Record<string, string> = Object.fromEntries(
  Object.entries(MORSE_TABLE).map(([ch, code]) => [code, ch]),
)

/**
 * Decodifica uma chave manual (segurar = tom, soltar = fim) em texto —
 * a direção OPOSTA de MorseStreamer: aqui é a duração de cada aperto que
 * decide ponto ou traço, não o texto que decide a duração do tom.
 *
 * `keyDown`/`keyUp` só medem tempo; `flush` decodifica pela tabela
 * invertida quando o silêncio dura o suficiente pra significar "essa letra
 * acabou" — os mesmos múltiplos de `dit` que MorseStreamer usa pra gerar
 * gaps (3×dit = fim de letra, mais 4×dit = fim de palavra, total 7×dit
 * desde a soltura), só que aqui como temporizadores reais em vez de passos
 * de áudio pré-calculados.
 */
export class ManualKeyer {
  private readonly dit: number
  private readonly onChar: (ch: string) => void
  private pattern = ''
  private downAt = 0
  private charTimer: number | null = null
  private wordTimer: number | null = null

  constructor(wpm: number, onChar: (ch: string) => void) {
    this.dit = ditMs(wpm)
    this.onChar = onChar
  }

  keyDown(nowMs: number): void {
    this.downAt = nowMs
    this.clearTimers()
  }

  /** Menos de 2 dits de duração é ponto; 2 ou mais é traço — o mesmo limiar
   * didático usado no resto do CW desta sessão, não um número inventado à
   * parte. */
  keyUp(nowMs: number): void {
    const dur = nowMs - this.downAt
    this.pattern += dur < this.dit * 2 ? '.' : '-'
    this.clearTimers()
    this.charTimer = window.setTimeout(() => this.flushChar(), this.dit * 3)
  }

  /**
   * Decodifica o que já foi apertado AGORA, sem esperar o gap natural —
   * usado quando a transmissão termina no meio de uma letra, pra não
   * comer a última letra silenciosamente. Ao contrário de flushChar, não
   * agenda um espaço depois: a sessão está terminando, não há "depois".
   */
  flush(): void {
    this.clearTimers()
    this.decodePattern()
  }

  private flushChar(): void {
    this.decodePattern()
    this.wordTimer = window.setTimeout(() => this.onChar(' '), this.dit * 4)
  }

  private decodePattern(): void {
    if (!this.pattern) return
    this.onChar(REVERSE_MORSE[this.pattern] ?? '?')
    this.pattern = ''
  }

  private clearTimers(): void {
    if (this.charTimer !== null) window.clearTimeout(this.charTimer)
    if (this.wordTimer !== null) window.clearTimeout(this.wordTimer)
    this.charTimer = this.wordTimer = null
  }
}
